import React, { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Link,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";
import PasswordField from "bundles/common/components/PasswordField";
import { useSnackbar } from "notistack";
import OauthButton from "bundles/common/components/OauthButton";
import TextDivider from "bundles/common/components/TextDivider";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { replaceCurrentCandidate } from "bundles/Candidate/constants/candidateConstants";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants.js";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const Login = ({ onSectionClicked, onActionFinished, customScope }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const dispatch = useDispatch();

  const initialFormValues = {
    email: "",
    password: "",
    remember_me: false,
  };

  const location = useLocation();
  const oauth_error = new URLSearchParams(location.search).get("oauth_error");

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [oauthError, setOauthError] = useState(oauth_error);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
    }

    if ("password" in fieldValues) {
      temp.password = fieldValues.password ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleCheckboxValue = (e) => {
    const { name, checked } = e.target;
    setValues({
      ...values,
      [name]: checked,
    });
  };

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    if (validate(values)) {
      sh.post(`/${i18nLocale}/candidates/sign_in`, {
        authenticity_token,
        candidate: {
          email: values.email,
          password: values.password,
          remember_me: values.remember_me,
        },
      })
        .then((res) => {
          setLoading(false);
          if (res.data.company_id) {
            replaceCurrentRecruiter(dispatch, authenticity_token, () => {
              window.location.href = window.location.origin;
            });
          } else {
            replaceCurrentCandidate(
              dispatch,
              authenticity_token,
              onActionFinished
            );
          }
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status == 401) {
            enqueueSnackbar(
              intl.formatMessage({
                id: "devise.login.unauthorized",
              }),
              { variant: "error" }
            );
          }
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={{ mt: "24px" }}>
      <Grid item xs={12}>
        <Box>
          <Box sx={{ mx: "8px" }}>
            <Grid container justifyContent="center">
              <Grid item xs={12}>
                <Box sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}login`}
                    provider="linkedin"
                  />
                  {oauthError && oauthError == "linkedin_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}login`}
                    provider="facebook"
                  />
                  {oauthError && oauthError == "facebook_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}login`}
                    provider="microsoft"
                  />
                  {oauthError && oauthError == "microsoft_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: "16px", mb: "16px" }}>
                  <TextDivider labelId="oauth.divider" />
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container justifyContent="center">
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="email"
                        variant="outlined"
                        onBlur={handleInputValue}
                        onChange={handleInputValue}
                        name="email"
                        sx={inputSx}
                        {...(errors["email"] && {
                          error: true,
                          helperText: errors["email"],
                        })}
                        label={<FormattedMessage id="devise.signup.email" />}
                      />
                      <PasswordField
                        name="password"
                        labelId="devise.signup.password"
                        handleInputValue={handleInputValue}
                        errors={errors}
                      />
                      <Box sx={{ mb: "8px" }}>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.75rem",
                            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          <Link
                            onClick={() =>
                              onSectionClicked("forgotten_password")
                            }
                            underline="always"
                            sx={{
                              cursor: "pointer",
                              color: "hsl(153 68% 25%)",
                              textDecorationColor: "hsl(153 68% 25% / 0.4)",
                              "&:hover": {
                                textDecorationColor: "hsl(153 68% 25%)",
                              },
                            }}
                          >
                            <FormattedMessage id="devise.forgotten_password" />
                          </Link>
                        </Box>
                      </Box>

                      <FormControlLabel
                        control={
                          <Checkbox
                            value={values.remember_me}
                            onChange={handleCheckboxValue}
                            name="remember_me"
                            sx={{
                              color: "hsl(220, 13%, 80%)",
                              "&.Mui-checked": {
                                color: "hsl(153 68% 25%)",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.875rem",
                              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                              color: "hsl(220, 10%, 46%)",
                            }}
                          >
                            <FormattedMessage id="devise.login.remember_me" />
                          </Box>
                        }
                      />

                      <Button
                        fullWidth
                        type="submit"
                        size="medium"
                        disabled={loading}
                        sx={{
                          bgcolor: "hsl(153 68% 25%)",
                          color: "white",
                          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          px: "1.5rem",
                          py: "0.75rem",
                          borderRadius: "0.75rem",
                          textTransform: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.5rem",
                          "&:hover": {
                            bgcolor: "hsl(153 68% 25% / 0.9)",
                          },
                          "&.Mui-disabled": {
                            bgcolor: "hsl(153 68% 25% / 0.3)",
                            color: "hsl(0 0% 100% / 0.6)",
                          },
                        }}
                      >
                        <Box justifyContent="center">
                          {loading && (
                            <CircularProgress size={16} sx={{ color: "white" }}>
                              <FormattedMessage id="signin" />
                            </CircularProgress>
                          )}
                          {!loading && <FormattedMessage id="signin" />}
                        </Box>
                        {!loading && <ArrowRight size={14} />}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{ mt: "16px", textAlign: "center" }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 10%, 46%)",
                  }}
                >
                  <FormattedMessage
                    align="center"
                    id="devise.not_yet_subscribed"
                  />{" "}
                  <Link
                    onClick={() => onSectionClicked("sign_up")}
                    underline="always"
                    sx={{
                      cursor: "pointer",
                      color: "hsl(153 68% 25%)",
                      textDecorationColor: "hsl(153 68% 25% / 0.4)",
                      "&:hover": {
                        textDecorationColor: "hsl(153 68% 25%)",
                      },
                    }}
                  >
                    <FormattedMessage id="signup" />
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    color: "hsl(220, 10%, 46%)",
                  }}
                >
                  <FormattedMessage
                    align="center"
                    id="devise.did_not_receive_confirmation"
                  />{" "}
                  <Link
                    onClick={() => onSectionClicked("resend_confirmation")}
                    underline="always"
                    sx={{
                      cursor: "pointer",
                      color: "hsl(153 68% 25%)",
                      textDecorationColor: "hsl(153 68% 25% / 0.4)",
                      "&:hover": {
                        textDecorationColor: "hsl(153 68% 25%)",
                      },
                    }}
                  >
                    <FormattedMessage id="click_here" />
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
